/* Operator Smoke ✦ — pre-event health check.
 *
 * Calm, single-tap board for a stressed operator to verify the
 * evening is solid before the doors open. Mobile-first, no auth
 * complexity, instant green/red feedback.
 *
 * Designed to be re-run as many times as needed — every check is
 * read-only. The "Run again ✦" button refetches without a page
 * reload so the operator can hold their phone over the QR plate
 * and tap repeatedly.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INK = '#0c0805';
const INK_2 = '#1a120a';
const IVORY = '#f5efdf';
const IVORY_SOFT = 'rgba(245,239,223,0.74)';
const GOLD = '#c19a4b';
const GREEN = '#7eab6e';
const AMBER = '#d6a64a';
const RED = '#c5614a';
const HAIRLINE = 'rgba(245,239,223,0.16)';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const STATUS_COLOR = { pass: GREEN, warn: AMBER, fail: RED };
const STATUS_GLYPH = { pass: '●', warn: '◆', fail: '✕' };
const STATUS_LABEL = { pass: 'OK', warn: 'WARN', fail: 'FAIL' };

function StatusGlyph({ status }) {
  return (
    <span
      data-testid={`smoke-status-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: '50%',
        background: STATUS_COLOR[status] + '22',
        color: STATUS_COLOR[status], fontSize: 14, fontWeight: 700,
        border: `1px solid ${STATUS_COLOR[status]}55`,
        flexShrink: 0,
      }}
    >{STATUS_GLYPH[status] || '?'}</span>
  );
}

function CheckRow({ check, idx }) {
  return (
    <li
      data-testid={`smoke-row-${idx}`}
      data-status={check.status}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        padding: '14px 0',
        borderBottom: `1px solid ${HAIRLINE}`,
      }}
    >
      <StatusGlyph status={check.status} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT_DISPLAY, fontSize: 17, color: IVORY,
          lineHeight: 1.3, marginBottom: 4,
        }}>{check.name}</div>
        <div style={{
          fontFamily: FONT_BODY, fontSize: 12.5,
          color: IVORY_SOFT, lineHeight: 1.5,
          wordBreak: 'break-word',
        }}>{check.detail}</div>
      </div>
      <span style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.2em',
        color: STATUS_COLOR[check.status], flexShrink: 0, marginTop: 6,
      }}>{STATUS_LABEL[check.status]}</span>
    </li>
  );
}

export default function OperatorSmokePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const run = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.get(`${API}/operator/smoke/${slug}`);
      setData(r.data);
      setLastRun(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not run smoke test');
    } finally {
      setBusy(false);
    }
  }, [slug]);

  useEffect(() => { run(); }, [run]);

  const summary = data?.summary;
  // Overall status: any fail → fail, any warn → warn, else pass
  const overall = !summary ? 'busy'
    : summary.fail > 0 ? 'fail'
    : summary.warn > 0 ? 'warn'
    : 'pass';

  return (
    <div
      data-testid="operator-smoke-page"
      data-overall={overall}
      style={{
        minHeight: '100vh',
        background: `radial-gradient(ellipse at 50% 30%, ${INK_2} 0%, ${INK} 75%)`,
        color: IVORY, fontFamily: FONT_BODY,
        padding: '20px 18px 80px',
      }}
    >
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {/* Header */}
        <header style={{ marginBottom: 22 }}>
          <p style={{
            margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.28em',
            textTransform: 'uppercase', color: GOLD,
          }}>✦ Operator Smoke</p>
          <h1
            data-testid="smoke-title"
            style={{
              fontFamily: FONT_DISPLAY, fontSize: 32, lineHeight: 1.1,
              margin: '10px 0 4px', color: IVORY,
            }}
          >
            Is the evening ready?
          </h1>
          {data?.event_name && (
            <p data-testid="smoke-event-name" style={{
              margin: '4px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 15, color: IVORY_SOFT,
            }}>{data.event_name}</p>
          )}
        </header>

        {/* Overall status banner */}
        {summary && (
          <div
            data-testid="smoke-overall"
            data-overall={overall}
            style={{
              padding: '18px 18px',
              border: `1px solid ${STATUS_COLOR[overall === 'busy' ? 'warn' : overall]}55`,
              background: STATUS_COLOR[overall === 'busy' ? 'warn' : overall] + '18',
              borderRadius: 8,
              marginBottom: 20,
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              fontFamily: FONT_DISPLAY, fontSize: 22, color: IVORY,
            }}>
              <StatusGlyph status={overall === 'busy' ? 'warn' : overall} />
              <span>
                {overall === 'pass' && 'The evening is solid ✦'}
                {overall === 'warn' && 'Mostly ready — a few quiet notes ✦'}
                {overall === 'fail' && 'Action needed before guests arrive ✦'}
              </span>
            </div>
            <div style={{
              marginTop: 10, fontSize: 12,
              color: IVORY_SOFT, letterSpacing: '0.04em',
            }}>
              <span data-testid="smoke-pass-count">{summary.pass} OK</span>
              {' · '}
              <span data-testid="smoke-warn-count">{summary.warn} warn</span>
              {' · '}
              <span data-testid="smoke-fail-count">{summary.fail} fail</span>
              {' · '}
              <span>{summary.elapsed_ms}ms</span>
            </div>
          </div>
        )}

        {err && (
          <div data-testid="smoke-error" style={{
            padding: 16, borderRadius: 8, marginBottom: 20,
            background: RED + '18', border: `1px solid ${RED}55`,
            color: '#f4b8b0', fontSize: 14, fontStyle: 'italic',
          }}>{err}</div>
        )}

        {busy && !data && (
          <div data-testid="smoke-loading" style={{
            padding: '40px 0', textAlign: 'center',
            fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 18, color: IVORY_SOFT,
          }}>Listening to the room…</div>
        )}

        {/* Checks list */}
        {data?.checks && (
          <ul data-testid="smoke-checks" style={{
            listStyle: 'none', margin: 0, padding: 0,
            borderTop: `1px solid ${HAIRLINE}`,
          }}>
            {data.checks.map((c, i) => <CheckRow key={i} check={c} idx={i} />)}
          </ul>
        )}

        {/* Re-run button */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button
            onClick={run}
            disabled={busy}
            data-testid="smoke-rerun"
            style={{
              padding: '14px 26px', borderRadius: 999, border: 'none',
              cursor: busy ? 'default' : 'pointer',
              background: `linear-gradient(180deg, ${GOLD}, #9a7836)`,
              color: '#1a0f04', fontWeight: 700, letterSpacing: '0.06em',
              fontSize: 14, opacity: busy ? 0.6 : 1, minWidth: 200,
            }}
          >{busy ? 'Listening…' : 'Run again ✦'}</button>
          {lastRun && (
            <p style={{
              margin: '14px 0 0', fontSize: 11, color: IVORY_SOFT,
              letterSpacing: '0.12em', textTransform: 'uppercase',
            }}>
              Last run · {lastRun.toLocaleTimeString()}
            </p>
          )}
        </div>

        <p style={{
          marginTop: 36, textAlign: 'center', fontSize: 10.5,
          color: 'rgba(245,239,223,0.32)', letterSpacing: '0.16em',
        }}>Operator Smoke ✦ · read-only · safe to re-run</p>
      </div>
    </div>
  );
}
